import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  fadeUp,
  fadeUpFar,
  fadeUpFarther,
  imageReveal,
  staggerContainer,
  staggerItem,
  viewportOnce,
} from '../hooks/useAnimations'
import StateCarousel from '../components/StateCarousel'
import './StatePage.css'

function getNeighbor(states, slug) {
  return states.find((s) => s.slug === slug) ?? null
}

function isCurrentPostedSection(heading) {
  const normalized = heading.trim().toLowerCase()
  return normalized === 'current posted design' || normalized === 'current posted designs'
}

function getPostedImages(state) {
  if (state.postedImages?.length) return state.postedImages
  if (!state.postedImage) return []
  return [{
    image: state.postedImage,
    credit: state.postedImageCredit,
    url: state.postedImageUrl,
  }]
}

function PostedShieldGallery({ state, name }) {
  const images = getPostedImages(state)
  if (!images.length) return null

  const multi = images.length > 1

  return (
    <div className={`state-posted-gallery${multi ? ' state-posted-gallery--multi' : ''}`}>
      {images.map((item) => (
        <div key={item.image} className="state-posted-item">
          {item.label && <p className="state-posted-label">{item.label}</p>}
          <div className="state-shield-wrap state-shield-wrap--posted">
            <div className="state-shield-image media-zoom media-zoom--plain">
              <img
                src={item.image}
                alt={item.label ?? `Current posted ${name} state route marker`}
              />
            </div>
            {item.url && (
              <p className="state-image-credit">
                <a href={item.url} target="_blank" rel="noopener noreferrer">
                  {item.credit ?? 'Source diagram'}
                </a>
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export default function StatePage({ state, states }) {
  const prev = state.prevSlug ? getNeighbor(states, state.prevSlug) : null
  const next = state.nextSlug ? getNeighbor(states, state.nextSlug) : null

  return (
    <main className="state-page">
      <div className="state-inner">
        <motion.nav className="state-breadcrumb" variants={fadeUp} initial="hidden" animate="visible">
          <Link to="/">Overview</Link>
          <span>/</span>
          <span>{state.name}</span>
        </motion.nav>

        <header className="state-hero">
          <motion.h1 className="page-title" variants={fadeUpFarther} initial="hidden" animate="visible">
            {state.name}
          </motion.h1>
        </header>

        <motion.div
          className="state-shield-wrap"
          variants={imageReveal}
          initial="hidden"
          animate="visible"
        >
          <div className="state-shield-image media-zoom media-zoom--plain">
            <img src={state.image} alt={`Redesigned ${state.name} state route marker`} />
          </div>
        </motion.div>

        <motion.div
          className="state-sections"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          {state.intro.map((paragraph, i) => (
            <motion.p key={`intro-${i}`} className="state-body" variants={fadeUpFar}>
              {paragraph}
            </motion.p>
          ))}

          {state.sections.map((section) => (
            <motion.section key={section.heading} className="state-section" variants={staggerItem}>
              <h2 className="state-section-heading">{section.heading}</h2>
              {isCurrentPostedSection(section.heading) && (
                <>
                  {state.postedDesignNote && (
                    <p className="state-body state-posted-note">{state.postedDesignNote}</p>
                  )}
                  <PostedShieldGallery state={state} name={state.name} />
                </>
              )}
              {section.paragraphs.map((paragraph, i) => (
                <p key={i} className="state-body">
                  {paragraph}
                </p>
              ))}
            </motion.section>
          ))}
        </motion.div>

        <StateCarousel
          prevSlug={state.prevSlug}
          nextSlug={state.nextSlug}
          prevName={prev?.name}
          nextName={next?.name}
        />
      </div>
    </main>
  )
}
